import {
  Dialog,
  Grid,
  IconButton,
  Slide,
  Theme,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import {
  BasePage,
  Footer,
  backgroundThemes,
  colors,
  dimensions,
  themeMethods,
} from 'design-system';
import React, { useEffect, useMemo } from 'react';
import { XCircle } from 'react-feather';
import { ConnectedProps, connect } from 'react-redux';

import {
  COMPLETE_VEHICLE,
  THIRD_PARTY_PACK,
  THIRD_PARTY_VEHICLE,
} from 'data-layer-cr/dist/constants/products';
import { matchCurrentDate } from 'data-layer-cr/dist/functions/helpers';
import {
  defaultUnitDriver,
  mustShowAllUnits,
} from 'data-layer-cr/dist/functions/quotations';
import { ProductCoverageCatalog, Quotation } from 'data-layer-cr/dist/models';

// Redux related
import { requestCoveragesForProduct } from 'src/store/coverage/actions';
import { RootState } from 'src/store/rootReducer';

// Models
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';

// Functions
import driverAndCustomerAreDifferent from 'src/functions/driverAndCustomerAreDifferent';
import { hasUnits } from 'src/functions/quotationMethods';

// Components
import { displayHeaderContent } from '../../functions/displayContent';
import { useCoverageColectiveResume } from '../../hooks';
import {
  IndividualDetails,
  MultipleOptionVehicle,
  MultipleOptionVehicleRC,
  RegularDetails,
} from '../DetailsContent';
import QuotationDetailsButton from '../QuotationDetailButtons';

export const CLOSE_ICON_SIZE = 24;

const useStyles = makeStyles((theme: Theme) => {
  const grey =
    'linear-gradient(359.66deg, rgba(242, 244, 243,0.4) 13.26%, rgba(255, 255, 255,0.4) 88.97%)';
  const paperBackgroundColor =
    'linear-gradient(359.66deg, rgb(242, 244, 243) 13.26%, #FFFFFF 88.97%)';
  return createStyles({
    root: {
      background: paperBackgroundColor,
    },
    container: {
      flex: 1,
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundImage: `${grey}, ${themeMethods.getCurrentMonthTheme(
        new Date(),
        backgroundThemes.QUOTATION_PAGE,
      )}`,
      [theme.breakpoints.up('lg')]: {
        backgroundPosition: ({ productType }: { productType: string }) =>
          productType === THIRD_PARTY_VEHICLE ? 'bottom' : 'initial',
      },
      [theme.breakpoints.down('md')]: {
        backgroundPosition: 'bottom',
      },
      [theme.breakpoints.down('sm')]: {
        backgroundImage: 'none',
      },
    },
    mainContainer: {
      [theme.breakpoints.up('lg')]: {
        paddingRight: 10,
        paddingLeft: 10,
      },
    },
    closeIcon: {
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        right: dimensions.desktopSidePadding + CLOSE_ICON_SIZE,
        top: '50%',
        marginTop: -CLOSE_ICON_SIZE / 2.0,
      },
    },
  });
});

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Slide direction="up" ref={ref} {...props} />;
});

export const QuotationDetailsDialogue = ({
  onCloseRequested,
  quotation,
  open,
  productCoverages,
  requestCoveragesForProductAction,
  currentProfile,
}: Props): JSX.Element => {
  const productType = quotation.productId;
  const classes = useStyles({ productType });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  /** Header Title */
  const headerTitle: React.ReactNode = displayHeaderContent();

  const { coverages, coverageResumes, additionalBenefits } =
    useCoverageColectiveResume({
      coveragesForProduct: productCoverages,
      productType,
    });

  const driver = useMemo(() => defaultUnitDriver(quotation), [quotation]);
  const differentDriver = useMemo(
    () =>
      (driverAndCustomerAreDifferent(driver, quotation.customer) ||
        quotation.customerType === 'J') &&
      !quotation.createdBy.includes('SOAT_'),
    [driver, quotation],
  );

  /**
   * The actual content, adjusted for the quotation's configuration
   */
  const detailsContent = useMemo(() => {
    if (mustShowAllUnits(quotation)) {
      if (quotation.productId === COMPLETE_VEHICLE) {
        return (
          <MultipleOptionVehicle
            data-testid="multiple-options-vehicle"
            quotation={quotation}
            coverageCatalogs={coverages}
            coverageCatalogResumes={coverageResumes}
            differentDriver={differentDriver}
          />
        );
      }
      if (quotation.productId === THIRD_PARTY_PACK) {
        return (
          <MultipleOptionVehicleRC
            data-testid="multiple-options-vehicle-rc"
            quotation={quotation}
            coverageCatalogs={coverages}
            coverageCatalogResumes={coverageResumes}
            differentDriver={differentDriver}
          />
        );
      }
    }

    if (currentProfile.type === 'colectiva') {
      return (
        <RegularDetails
          data-testid="regular-details"
          quotation={quotation}
          coverageCatalogs={coverages}
          coverageCatalogResumes={coverageResumes}
          differentDriver={differentDriver}
          currentProfile={currentProfile}
          additionalBenefits={additionalBenefits}
        />
      );
    }

    return (
      <IndividualDetails
        data-testid="individual-details"
        quotation={quotation}
        coverageCatalogs={coverages}
        coverageCatalogResumes={coverageResumes}
        currentProfile={currentProfile}
      />
    );
  }, [
    quotation,
    coverages,
    coverageResumes,
    differentDriver,
    currentProfile,
    additionalBenefits,
  ]);

  useEffect(() => {
    if (
      (coverages.length <= 0 || coverageResumes.length <= 0) &&
      productType !== ''
    ) {
      requestCoveragesForProductAction(
        productType,
        quotation.units[0].unit.use,
      );
    }
  }, [
    coverages,
    coverageResumes,
    productType,
    requestCoveragesForProductAction,
    currentProfile,
    quotation,
  ]);

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={Transition}
      classes={{
        paper: classes.root,
      }}
    >
      <div className={classes.container}>
        <BasePage
          /** We have to check if a planType exists,
           * otherwise just display the prices */
          title={headerTitle}
          appBarPosition="sticky"
          rightActions={
            <IconButton
              id="sales-web-button-close"
              data-testid="close-button"
              className={classes.closeIcon}
              onClick={onCloseRequested}
            >
              <XCircle size={CLOSE_ICON_SIZE} color={colors.white} />
            </IconButton>
          }
        >
          <Grid container className={classes.mainContainer}>
            {hasUnits(quotation) && (
              <Grid data-testid="content-wrapper" item xs={12}>
                {detailsContent}
              </Grid>
            )}
            <Grid item xs={12}>
              {!matchCurrentDate(quotation.expireAt) && (
                <QuotationDetailsButton
                  data-testid="details-button"
                  quotation={quotation}
                />
              )}
            </Grid>
          </Grid>
        </BasePage>
      </div>
      {/** Footer Section */}
      <Footer
        IsMobile={isMobile}
        rootStyles={{ backgroundColor: 'transparent' }}
        country="Costa Rica"
        linkText="Consulte nuestros términos y condiciones"
        linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
      />
    </Dialog>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  productCoverages: ProductCoverageCatalog;
  currentProfile: PricingProfileResponse;
} => ({
  productCoverages: state.coverage.coveragesForProduct,
  currentProfile: state.quotation.currentProfile,
});
const reduxActions = {
  requestCoveragesForProductAction: requestCoveragesForProduct,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  open: boolean;
  quotation: Quotation;
  onCloseRequested: () => void;
  onBackRequested?: () => void;
}

export default connector(QuotationDetailsDialogue);
